import { useEffect, useState } from "react";
import { Users } from "lucide-react";
import { splitRaceName, ratingsOf, limitsOf, raceMs, clock12, tzLabel, countdown } from "@/lib/raceFormat";

export const useNow = (every = 30000) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), every); return () => clearInterval(t); }, [every]);
  return now;
};

export const isFinished = (race, ms, now) => Boolean(race.result) || (ms != null && now > ms + 15 * 60000);

const Tile = ({ label, value, testid }) => (
  <div className="rounded-lg bg-[#0B101D] border border-[#202E4C] px-3 py-2.5" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className="text-base font-semibold text-slate-100 mt-0.5 leading-snug break-words">{value ?? "—"}</p>
  </div>
);

export const RaceHeader = ({ race, date, onTips, onResults, now }) => {
  const ms = raceMs(race, date);
  const done = isFinished(race, ms, now);
  const { sponsor, title } = splitRaceName(race.name || "");
  const rt = ratingsOf(race);
  const limits = limitsOf(race.name || "");
  const field = race.runners.filter((r) => !r.scratched).length;
  return (
    <>
      <div className="flex items-center gap-3">
        <span className="px-2 py-0.5 rounded-md bg-blue-600 text-white text-xs font-bold" data-testid="race-number-badge">R{race.number}</span>
        {race.start_time && (
          <span className="flex items-baseline gap-1.5" data-testid="race-start-time">
            <span className="text-xl font-bold text-slate-50">{clock12(ms)}</span>
            <span className="text-[11px] font-semibold text-slate-500">{tzLabel(ms)}</span>
          </span>
        )}
        <span className="ml-auto text-sm font-semibold" data-testid="race-countdown">
          {done ? (
            <span className="flex items-center gap-2 text-slate-400">Finished
              {race.result && <button data-testid="race-results-link" onClick={onResults} className="text-blue-400 hover:underline">Results</button>}
            </span>
          ) : ms ? <span className="text-blue-400">{countdown(ms, now)}</span> : null}
        </span>
      </div>
      {sponsor && <p className="mt-3 text-xs text-slate-500 break-words" data-testid="race-sponsor">{sponsor}</p>}
      <h2 className={`${sponsor ? "mt-0.5" : "mt-3"} font-display text-lg sm:text-xl font-bold text-slate-50 break-words`} data-testid="race-name">{title}</h2>
      {limits.length > 0 && (
        <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-400" data-testid="race-limits">
          <Users className="w-3.5 h-3.5 text-blue-400" aria-hidden />{limits.join(" · ")}
        </p>
      )}
      <div className="grid grid-cols-2 gap-2 mt-4" data-testid="race-stats">
        <Tile label="Distance" value={race.distance ? `${race.distance}m` : null} testid="race-stat-distance" />
        <Tile label="Prize money" value={race.prize ? `$${Number(race.prize).toLocaleString("en-AU")}` : null} testid="race-stat-prize" />
        <Tile label="Runners" value={field} testid="race-stat-runners" />
        <Tile label={rt?.label || "Ratings"} value={rt?.value} testid="race-stat-ratings" />
      </div>
      <button data-testid="view-race-tips-button" onClick={onTips}
        className="mt-4 w-full rounded-full bg-blue-600 hover:bg-blue-700 active:scale-[0.99] transition-[background-color,transform] text-white font-semibold py-3 shadow-[0_0_12px_rgba(37,99,235,0.3)]">
        View R{race.number} tips
      </button>
    </>
  );
};
